/**
 * Glass-styled text input for the learner experience.
 * Wraps React Aria TextField for validation, labelling, and accessibility.
 *
 * Supports: single-line input, multi-line textarea, leading/trailing icons,
 * label, description, error messages, and sizes.
 */
import { useState, type ReactNode, type CSSProperties } from 'react';
import {
  TextField as AriaTextField,
  SearchField as AriaSearchField,
  Input,
  TextArea as AriaTextArea,
  Label,
  Text,
  Button as AriaButton,
  type TextFieldProps as AriaTextFieldProps,
  type SearchFieldProps as AriaSearchFieldProps,
} from 'react-aria-components';
import { Search, X } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

export type TextFieldSize = 'sm' | 'md' | 'lg';

interface TextFieldBaseProps {
  label?: string;
  description?: string;
  errorMessage?: string;
  size?: TextFieldSize;
  icon?: ReactNode;
  iconRight?: ReactNode;
  placeholder?: string;
  className?: string;
  style?: CSSProperties;
}

export type TextFieldProps = TextFieldBaseProps & {
  multiline?: boolean;
  rows?: number;
} & Omit<AriaTextFieldProps, 'style' | 'className' | 'children'>;

export type SearchFieldProps = TextFieldBaseProps &
  Omit<AriaSearchFieldProps, 'style' | 'className' | 'children'>;

// ─── Size config ──────────────────────────────────────────────────────────────

const sizeConfig: Record<
  TextFieldSize,
  {
    height: number | string;
    fontSize: number | string;
    padding: string;
    iconSize: number;
    labelSize: number | string;
    radius: number | string;
  }
> = {
  sm: {
    height: 'var(--control-height-sm)',
    fontSize: 'var(--text-xs)',
    padding: '0 14px',
    iconSize: 15,
    labelSize: 'var(--text-xs)',
    radius: 'var(--control-radius-sm)',
  },
  md: {
    height: 'var(--control-height-md)',
    fontSize: 'var(--text-sm)',
    padding: '0 16px',
    iconSize: 16,
    labelSize: 'var(--text-xs)',
    radius: 'var(--control-radius-md)',
  },
  lg: {
    height: 'var(--control-height-lg)',
    fontSize: 'var(--text-base)',
    padding: '0 18px',
    iconSize: 17,
    labelSize: 'var(--text-sm)',
    radius: 'var(--control-radius-lg)',
  },
};

// ─── Shared styles ────────────────────────────────────────────────────────────

function getWrapperStyle(
  s: (typeof sizeConfig)[TextFieldSize],
  isFocused: boolean,
  isInvalid: boolean,
  isDisabled: boolean,
  multiline: boolean
): CSSProperties {
  const borderColor = isInvalid
    ? 'var(--color-error)'
    : isFocused
      ? 'var(--color-accent)'
      : 'var(--color-surface-border)';

  const ringColor = isInvalid ? 'var(--color-error-subtle)' : 'var(--color-accent-subtle)';

  return {
    display: 'flex',
    alignItems: multiline ? 'flex-start' : 'center',
    gap: 8,
    minHeight: multiline ? undefined : s.height,
    padding: multiline ? '10px 14px' : s.padding,
    borderRadius: s.radius,
    background: 'var(--color-surface-solid)',
    border: `1px solid ${borderColor}`,
    boxShadow: isFocused
      ? `0 0 0 3px ${ringColor}, inset 0 0.5px 0 var(--color-surface-highlight)`
      : 'var(--control-shadow-inset)',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    opacity: isDisabled ? 0.5 : 1,
    cursor: isDisabled ? 'not-allowed' : undefined,
    fontFamily: 'var(--font-sans)',
  };
}

const inputReset: CSSProperties = {
  flex: 1,
  minWidth: 0,
  background: 'transparent',
  border: 'none',
  outline: 'none',
  fontFamily: 'inherit',
  color: 'var(--color-text)',
  letterSpacing: '-0.01em',
};

// ─── Label + description + error ──────────────────────────────────────────────

function FieldLabel({ children, size }: { children: ReactNode; size: TextFieldSize }) {
  const s = sizeConfig[size];
  return (
    <Label
      style={{
        display: 'block',
        fontSize: s.labelSize,
        fontWeight: 'var(--weight-semibold)',
        color: 'var(--color-text-secondary)',
        marginBottom: 6,
        fontFamily: 'var(--font-sans)',
      }}
    >
      {children}
    </Label>
  );
}

function FieldDescription({ children }: { children: ReactNode }) {
  return (
    <Text
      slot="description"
      style={{
        display: 'block',
        fontSize: 13,
        fontWeight: 'var(--weight-normal)',
        color: 'var(--color-text-secondary)',
        marginTop: 5,
        fontFamily: 'var(--font-sans)',
      }}
    >
      {children}
    </Text>
  );
}

function FieldError({ children }: { children: ReactNode }) {
  return (
    <Text
      slot="errorMessage"
      style={{
        display: 'block',
        fontSize: 13,
        fontWeight: 'var(--weight-medium)',
        color: 'var(--color-error-text)',
        marginTop: 5,
        fontFamily: 'var(--font-sans)',
      }}
    >
      {children}
    </Text>
  );
}

// ─── TextField ────────────────────────────────────────────────────────────────

export function TextField({
  label,
  description,
  errorMessage,
  size = 'md',
  icon,
  iconRight,
  placeholder,
  multiline,
  rows = 3,
  className,
  style,
  ...ariaProps
}: TextFieldProps) {
  const s = sizeConfig[size];
  const [focused, setFocused] = useState(false);

  return (
    <AriaTextField {...ariaProps} className={className} style={{ ...style }}>
      {(renderProps) => {
        const wrapperStyle = getWrapperStyle(
          s,
          focused,
          renderProps.isInvalid,
          renderProps.isDisabled,
          !!multiline
        );

        return (
          <>
            {label && <FieldLabel size={size}>{label}</FieldLabel>}
            <div style={wrapperStyle}>
              {icon && (
                <span style={{ flexShrink: 0, color: 'var(--color-text-muted)', display: 'flex' }}>
                  {icon}
                </span>
              )}
              {multiline ? (
                <AriaTextArea
                  rows={rows}
                  placeholder={placeholder}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  style={{
                    ...inputReset,
                    fontSize: s.fontSize,
                    resize: 'vertical',
                    lineHeight: 1.5,
                  }}
                />
              ) : (
                <Input
                  placeholder={placeholder}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  style={{
                    ...inputReset,
                    fontSize: s.fontSize,
                    height: '100%',
                  }}
                />
              )}
              {iconRight && (
                <span style={{ flexShrink: 0, color: 'var(--color-text-muted)', display: 'flex' }}>
                  {iconRight}
                </span>
              )}
            </div>
            {description && !renderProps.isInvalid && (
              <FieldDescription>{description}</FieldDescription>
            )}
            {errorMessage && renderProps.isInvalid && <FieldError>{errorMessage}</FieldError>}
          </>
        );
      }}
    </AriaTextField>
  );
}

// ─── SearchField ──────────────────────────────────────────────────────────────
// Convenience wrapper that adds a search icon and clear button.

export function SearchField({
  label,
  description,
  size = 'md',
  placeholder = 'Search...',
  className,
  style,
  ...ariaProps
}: SearchFieldProps) {
  const s = sizeConfig[size];
  const [focused, setFocused] = useState(false);

  return (
    <AriaSearchField {...ariaProps} className={className} style={{ ...style }}>
      {(renderProps) => {
        const wrapperStyle = getWrapperStyle(
          s,
          focused,
          renderProps.isInvalid,
          renderProps.isDisabled,
          false
        );

        return (
          <>
            {label && <FieldLabel size={size}>{label}</FieldLabel>}
            <div style={wrapperStyle}>
              <Search
                size={s.iconSize}
                strokeWidth={2}
                style={{ flexShrink: 0, color: 'var(--color-text-muted)' }}
              />
              <Input
                placeholder={placeholder}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                style={{
                  ...inputReset,
                  fontSize: s.fontSize,
                  height: '100%',
                }}
              />
              <AriaButton
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 20,
                  height: 20,
                  borderRadius: 9999,
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  flexShrink: 0,
                  transition: 'background 0.15s',
                  padding: 0,
                }}
                className="hover:bg-[rgba(0,0,0,0.06)] data-[empty]:hidden"
              >
                <X size={13} strokeWidth={2} style={{ color: 'var(--color-text-muted)' }} />
              </AriaButton>
            </div>
            {description && <FieldDescription>{description}</FieldDescription>}
          </>
        );
      }}
    </AriaSearchField>
  );
}
